import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import Button from 'components/Button';
import DropdownButton from 'components/DropdownButton';
import CircleCheckbox from 'components/CircleCheckbox';
import StatusBadge from 'components/StatusBadge/StatusBadge';
import WithdrawalDaysBox from 'components/WithdrawalDaysBox';
// utils
import classnames from 'classnames/bind';
// styles
import styles from './FeedRationCard.module.scss';

const cn = classnames.bind(styles);

// Read-only ration card for the Admin "Feed Ration Setup" list (drag handle + Edit split button)
const FeedRationCard = ({ ration, dragHandleProps, isDragging, onToggleStatus, onDelete }, { formatMessage }) => {
  const { id, name, medicated, active, medications } = ration;
  const editUrl = `/admin/feed-rations/${id}/edit`;
  const statusLabel = <FormattedMessage id={`general.button.${active ? 'disable' : 'enable'}`} />;

  return (
    <div className={cn('FeedRationCard', { 'is-dragging': isDragging, 'is-disabled': !active })}>
      <div
        className={cn('drag-handle')}
        aria-label={formatMessage({ id: 'container.admin.feedRations.dragToReorder' })}
        {...dragHandleProps}
      >
        <i className="fa fa-dots-three-vertical" />
      </div>

      <div className={cn('card-body')}>
        <div className={cn('card-actions', 'show-for-large')}>
          <DropdownButton
            idKey={`feed-ration-${id}`}
            label={<FormattedMessage id="general.button.edit" />}
            buttonType="small light-gray"
            url={editUrl}
            dropDownData={[
              { label: statusLabel, onClick: () => onToggleStatus(ration) },
              { label: <FormattedMessage id="general.button.delete" />, onClick: () => onDelete(ration) },
            ]}
          />
        </div>

        <div className={cn('card-content')}>
          <div className={cn('main-column')}>
            <div className={cn('field')}>
              <span className={cn('field-label')}>
                <FormattedMessage id="container.admin.feedRations.rationName" />
              </span>
              <div className={cn('ration-name')}>
                <span>{name}</span>
                {!active && <StatusBadge status="disabled" small className={cn('status-badge')} />}
              </div>
            </div>

            <div className={cn('field')}>
              <span className={cn('field-label')}>
                <FormattedMessage id="container.admin.feedRations.medicatedFeedRation" />
              </span>
              <CircleCheckbox
                checked={medicated}
                disabled
                readOnly
                label={<FormattedMessage id="container.admin.feedRations.containsMedication" />}
              />
            </div>
          </div>

          {medicated && (
            <div className={cn('medications-column')}>
              <span className={cn('field-label')}>
                <FormattedMessage id="container.admin.feedRations.medications" />
              </span>
              <span className={cn('field-hint')}>
                <FormattedMessage id="container.admin.feedRations.medicationsHint" />
              </span>
              <span className={cn('field-label', 'sub-label')}>
                <FormattedMessage id="container.admin.feedRations.medication" />
              </span>

              {medications.map((medication) => (
                <div key={medication.id} className={cn('medication-row')}>
                  <div className={cn('medication-name')}>
                    <span>{medication.treatment_product.name}</span>
                    {!medication.treatment_product.active && (
                      <StatusBadge status="disabled" small className={cn('status-badge')} />
                    )}
                  </div>
                  <WithdrawalDaysBox
                    label={<FormattedMessage id="container.admin.feedRations.usaWithdrawalDays" />}
                    value={medication.withdrawal_value_us}
                    period={medication.withdrawal_period_us}
                  />
                  <WithdrawalDaysBox
                    label={<FormattedMessage id="container.admin.feedRations.exportWithdrawalDays" />}
                    value={medication.withdrawal_value_export}
                    period={medication.withdrawal_period_export}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={cn('mobile-actions', 'hide-for-large')}>
          <Link to={editUrl} className="button small light">
            <FormattedMessage id="general.button.edit" />
          </Link>
          <Button small default onClick={() => onToggleStatus(ration)}>{statusLabel}</Button>
          <Button small red onClick={() => onDelete(ration)}>
            <FormattedMessage id="general.button.delete" />
          </Button>
        </div>
      </div>
    </div>
  );
};

FeedRationCard.contextTypes = {
  formatMessage: T.func.isRequired,
};

FeedRationCard.propTypes = {
  ration: T.shape({
    id: T.number.isRequired,
    name: T.string.isRequired,
    medicated: T.bool.isRequired,
    active: T.bool.isRequired,
    medications: T.arrayOf(T.shape({
      id: T.number.isRequired,
      withdrawal_value_us: T.number,
      withdrawal_period_us: T.string,
      withdrawal_value_export: T.number,
      withdrawal_period_export: T.string,
      treatment_product: T.shape({
        id: T.number.isRequired,
        name: T.string.isRequired,
        active: T.bool,
      }).isRequired,
    })).isRequired,
  }).isRequired,
  dragHandleProps: T.object,
  isDragging: T.bool,
  onToggleStatus: T.func.isRequired,
  onDelete: T.func.isRequired,
};

FeedRationCard.defaultProps = {
  dragHandleProps: {},
  isDragging: false,
};

export default FeedRationCard;
